import { ConfigApi } from '@backstage/core-plugin-api';

export type SsoConfig = {
  baseUrl: string;
  frontendUrl: string;
  applicationId: string;
};

const TOKEN_KEY = 'sso_token';
const PENDING_KEY = 'sso_login_pending';

const DEFAULT_API_URL = 'https://apisso.obp.mx/api/v1';
const DEFAULT_FRONTEND_URL = 'https://sso.obp.mx';

export function readSsoConfig(config: ConfigApi): SsoConfig {
  return {
    baseUrl:
      config.getOptionalString('app.sso.apiUrl')?.replace(/\/$/, '') ||
      DEFAULT_API_URL,
    frontendUrl:
      config.getOptionalString('app.sso.frontendUrl')?.replace(/\/$/, '') ||
      DEFAULT_FRONTEND_URL,
    applicationId: config.getOptionalString('app.sso.applicationId') ?? '',
  };
}

export function isSsoEnabled(sso: SsoConfig): boolean {
  return !!sso.applicationId;
}

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function removeToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

/** Marca que el usuario inició flujo SSO (sobrevive redirects / remounts). */
export function markSsoLoginPending(): void {
  sessionStorage.setItem(PENDING_KEY, '1');
}

export function clearSsoLoginPending(): void {
  sessionStorage.setItem(PENDING_KEY, '');
  sessionStorage.removeItem(PENDING_KEY);
}

export function isSsoLoginPending(): boolean {
  return sessionStorage.getItem(PENDING_KEY) === '1';
}

/** Limpia JWT + flag de pending (usar en Sign out). */
export function clearSsoSession(): void {
  removeToken();
  clearSsoLoginPending();
}

export function isTokenExpired(token: string): boolean {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const exp = (payload.exp ?? 0) * 1000;
    return Date.now() >= exp;
  } catch {
    return true;
  }
}

const APP_ROLES = ['Administrador', 'Editor', 'Usuario'] as const;
export type AppRole = (typeof APP_ROLES)[number];

export function getCurrentUserRoles(sso: SsoConfig): AppRole[] {
  const token = getToken();
  if (!token || !sso.applicationId) return [];
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const app = payload.applications?.find(
      (a: { applicationId: string }) => a.applicationId === sso.applicationId,
    );
    const raw = (app?.roles ?? []) as string[];
    return raw
      .map(r => {
        const lower = String(r).toLowerCase();
        if (lower === 'admin' || lower === 'administrador')
          return 'Administrador' as const;
        if (lower === 'editor') return 'Editor' as const;
        if (lower === 'usuario') return 'Usuario' as const;
        return null;
      })
      .filter((r): r is AppRole => r !== null);
  } catch {
    return [];
  }
}

export function isAdmin(sso: SsoConfig): boolean {
  if (!isSsoEnabled(sso)) return true;
  return getCurrentUserRoles(sso).includes('Administrador');
}

/** returnUrl por defecto = origin/callback (contrato SSO). */
export function redirectToSSO(sso: SsoConfig, returnUrl?: string): void {
  markSsoLoginPending();
  const url = returnUrl ?? `${window.location.origin}/callback`;
  const ssoUrl = `${sso.baseUrl}/auth/sso?applicationId=${encodeURIComponent(
    sso.applicationId,
  )}&returnUrl=${encodeURIComponent(url)}`;
  window.location.href = ssoUrl;
}

export async function validateToken(
  sso: SsoConfig,
  token: string,
): Promise<boolean> {
  try {
    const res = await fetch(`${sso.baseUrl}/auth/validate`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'X-Application-Id': sso.applicationId,
      },
    });
    const json = await res.json();
    return json.data?.valid === true || json.valid === true;
  } catch {
    return false;
  }
}

/** Headers que ProxiedSignInPage envía a /api/auth/sso/refresh */
export function ssoAuthHeaders(sso: SsoConfig): HeadersInit {
  const token = getToken();
  if (!token) return {};
  return {
    Authorization: `Bearer ${token}`,
    'X-Application-Id': sso.applicationId,
  };
}
